import type { BlockStateUnionType } from '@unifyapps/defs/types/block';
import type { SlotContextSnapshot } from '../../../stores/GlobalStateStore/types/slotContext';
import { parseSlotContextKey } from '../../../stores/GlobalStateStore/utils/buildSlotContextKey';

type BlockStatesMap = Record<string, BlockStateUnionType | undefined>;

// Folds snapshots into a copy of `blockStates` so consumer-side bindings
// `{{tcId.slots.slotName.context.X}}` resolve via path traversal on
// `blockStates[tcId].slots[slotName].context`. The path mirrors TC
// `stateSchema()` exactly — without the `slots` wrapper the binding path
// doesn't exist on `blockStates[tcId]` and resolves undefined.
//
// Returns the SAME `blockStates` reference when no snapshot applies, so
// `ContextBuilder.canReuseBaseContext` cache keeps hitting in steady state.
// Touched TC entries get fresh references; unrelated TCs keep identity.
export function mergeSlotContextSnapshots(
  blockStates: BlockStatesMap,
  snapshots: Record<string, SlotContextSnapshot>,
): BlockStatesMap {
  let result: BlockStatesMap = blockStates;

  for (const lookupKey of Object.keys(snapshots)) {
    const parsed = parseSlotContextKey(lookupKey);
    if (!parsed) continue;
    const { tcInstanceId, slotName } = parsed;
    if (!(tcInstanceId in blockStates)) continue;

    const snapshot = snapshots[lookupKey];

    if (result === blockStates) {
      result = { ...blockStates };
    }

    const tcState = result[tcInstanceId];
    const tcStateRecord = (tcState ?? {}) as Record<string, unknown>;
    const existingSlotsRecord = (tcStateRecord.slots ?? {}) as Record<string, unknown>;
    const existingSlotEntry = existingSlotsRecord[slotName];
    const nextSlotEntry =
      existingSlotEntry && typeof existingSlotEntry === 'object'
        ? { ...(existingSlotEntry as Record<string, unknown>), context: snapshot }
        : { context: snapshot };

    result[tcInstanceId] = {
      ...tcStateRecord,
      slots: {
        ...existingSlotsRecord,
        [slotName]: nextSlotEntry,
      },
    } as BlockStateUnionType;
  }

  return result;
}
